import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import ua from '../ua';
import Logger from './Logger';

// const isAppIos = ua.getPlatform() === 'ios';
// const defaultDirectory = isAppIos ? Directory.Cache : Directory.Documents;
const defaultDirectory = Directory.Cache;

export default class FileService {
  static async initFolder(folderName) {
    await Filesystem.mkdir({
      path: folderName,
      directory: defaultDirectory,
      recursive: false,
    }).catch(() => null);
  }

  static async readdir(folder = '') {
    const result = await Filesystem.readdir({
      path: folder,
      directory: defaultDirectory,
    }).catch((error) => {
      Logger.error(new Error('Mobile FileService readdir failed'), { error, folder }, [
        'MobileFileServiceReaddirFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
      return null;
    });
    return result;
  }

  async writeFile(folder, fileName, blob, chunkSize) {
    let path = fileName;
    if (folder) {
      await this.constructor.initFolder(folder);
      path = `${folder}/${fileName}`;
    }
    if (ua.isMobileApp()) {
      await this.convertToBase64Chunks(blob, chunkSize, async (value, first) => {
        if (first) {
          await Filesystem.writeFile({
            path,
            directory: defaultDirectory,
            data: value,
          });
        } else {
          await Filesystem.appendFile({
            path,
            directory: defaultDirectory,
            data: value,
          });
        }
      });
    } else {
      await Filesystem.writeFile({
        path,
        directory: defaultDirectory,
        data: blob,
        encoding: Encoding.UTF8,
      });
    }
  }

  static async readFile(folder, fileName) {
    const directory = await Filesystem.getUri({
      path: folder,
      directory: defaultDirectory,
    }).catch((error) => {
      Logger.error(new Error('Mobile FileService getUri failed'), { error, folder, fileName }, [
        'MobileFileServiceGetUriFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
      return null;
    });
    const fileSrc = Capacitor.convertFileSrc(`${directory.uri}/${fileName}`);
    const directoryUri = directory.uri;
    const fileUri = `${directory.uri}/${fileName}`;
    const safeUrl = fileSrc;
    return {
      fileUri,
      directoryUri,
      success: safeUrl,
    };
  }

  // eslint-disable-next-line consistent-return
  static async deleteFile(path) {
    const res = await Filesystem.deleteFile({
      path,
      directory: defaultDirectory,
    });
    return res;
  }

  static convertToBase64(blob) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      // `_realReader` is exposed by the Cordova/Capacitor FileReader polyfill; name is fixed upstream.
      // eslint-disable-next-line no-underscore-dangle
      const realFileReader = reader._realReader;
      if (realFileReader) {
        reader = realFileReader;
      }
      reader.onerror = () => {
        const domError = reader.error;
        const name = (domError && domError.name) || 'FileReaderError';
        const message = (domError && domError.message) || 'FileReader failed to read blob';
        const error = new Error(`FileReader error: ${name} — ${message}`);
        error.name = name;
        reject(error);
      };
      reader.onabort = () => {
        reject(new Error('FileReader aborted'));
      };
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
  }

  async convertToBase64Chunks(blob, size, chunk) {
    const chunkSize = 1024 * 1024 * size;
    if (chunkSize % 6) {
      throw new Error('Chunksize must be a multiple of 6!');
    } else {
      const blobSize = blob.size;
      let remaining = blob;
      while (remaining.size > chunkSize) {
        // Chunks must be converted and appended sequentially to preserve file order.
        /* eslint-disable no-await-in-loop */
        const value = await this.constructor.convertToBase64(remaining.slice(0, chunkSize));
        await chunk(blobSize === remaining.size ? value : value.split(',')[1], blobSize === remaining.size);
        /* eslint-enable no-await-in-loop */
        remaining = remaining.slice(chunkSize);
      }
      const lastValue = await this.constructor.convertToBase64(remaining.slice(0, remaining.size));
      await chunk(lastValue.split(',')[1], blobSize === remaining.size);
    }
  }

  static base64ToBlob(base64, contentType = 'application/octet-stream') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }
}
